import { Fragment } from "react";
import { styled } from "styled-components";
import { Bars3Icon, UserIcon, ArrowRightStartOnRectangleIcon } from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import Logo from "../assets/logo.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useUI, MenuItems } from "../contexts";
import { useAuth } from "../contexts";

const Navbar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  max-height: 100px;
  min-height: 100px;
  font-weight: 400;
  padding: 0 60px;
  background: ${({ theme }) => theme.colors.blackAlmost};
  box-shadow: 0px 4px 4px #000000;

  @media (max-width: 767px) {
    padding: 0 20px;
    min-height: 72px;
  }
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 48px;
  flex: 1;

  @media (max-width: 1023px) {
    gap: 28px;
  }
`;

const DesktopNav = styled.nav`
  display: none;

  @media (min-width: 768px) {
    display: flex;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 20px;
`;

const NavItem = styled.li<{ $isActive?: boolean }>`
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  font-weight: 500;
  padding: 0 6px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  transition: color 0.3s ease, transform 0.3s ease;
  cursor: pointer;
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary : theme.colors.white};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const MobileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (min-width: 1024px) {
    display: none;
  }
`;

const IconButton = styled.button<{ $variant?: "primary" | "neutral" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background: ${({ theme, $variant }) =>
    $variant === "primary" ? theme.colors.primary : theme.colors.lightBlue};
  color: ${({ theme, $variant }) =>
    $variant === "primary" ? theme.colors.blue500 : theme.colors.white};
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);

  &:hover {
    opacity: 0.9;
  }
`;

const ActionButtons = styled.div`
  display: none;
  flex-direction: row;
  align-items: center;
  gap: 18px;

  @media (min-width: 1024px) {
    display: flex;
  }
`;

const OutlineButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 14px;
  gap: 10px;
  height: 38px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    background: transparent;
  }
`;

const FilledButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 14px;
  gap: 10px;
  height: 38px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.blue500};
  border: none;
  border-radius: 8px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    opacity: 0.9;
    color: ${({ theme }) => theme.colors.blue500};
  }
`;

const LogoImage = styled.img`
  height: 32px;
  display: block;

  @media (max-width: 767px) {
    height: 28px;
  }
`;

const MobileMenuPanel = styled.div`
  width: 100%;
  max-width: 360px;
  margin: 80px auto 0;
  padding: 32px 24px;
  border-radius: 24px;
  background: ${({ theme }) => theme.colors.lightBlue};
  box-shadow: 0 24px 45px rgba(0, 0, 0, 0.4);
`;

const MobileMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  align-items: center;
`;

const MobileMenuItem = styled.button<{ $isActive?: boolean }>`
  font-family: "Sofia Pro", sans-serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 4px 8px;
  background: transparent;
  border: none;
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary : theme.colors.white};
  text-align: center;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const UserName = styled.span`
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.white};
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface MenuItem {
  label: string;
  key: MenuItems | string;
}

const menuItems: MenuItem[] = [
  {
    label: "AKTUÁLNÍ AUKCE",
    key: MenuItems.Home,
  },
  {
    label: "VÝHERCI",
    key: MenuItems.Winners,
  },
  {
    label: "O NÁS",
    key: MenuItems.AboutUs,
  },
  {
    label: "NÁPOVĚDA",
    key: MenuItems.Help,
  },
  {
    label: "BLOG",
    key: MenuItems.Blog,
  },
  {
    label: "KONTAKT",
    key: MenuItems.Contact,
  },
];

const pathnameToMenuKey = (pathname: string): MenuItems | null => {
  if (pathname === "/" || pathname === `/${MenuItems.Home}`) return MenuItems.Home;
  const segment = pathname.split("/")[1];
  const match = Object.values(MenuItems).find((v) => v === segment);
  return match || null;
};

const MenuBar: React.FC = () => {
  const { setSelectedMenu, isMobileMenuOpen, setIsMobileMenuOpen } = useUI();
  const { user, logout } = useAuth();
  const isLoggedIn = !!user;
  const navigate = useNavigate();
  const location = useLocation();

  const activeMenuKey = pathnameToMenuKey(location.pathname);
  const userLabel = user?.displayName?.trim() || "Můj účet";

  const showDrawer = () => {
    setIsMobileMenuOpen(true);
  };

  const closeDrawer = () => {
    setIsMobileMenuOpen(false);
  };

  const handleMenuClick = (menuItemKey: MenuItems | string) => {
    if (menuItemKey !== MenuItems.LogOut) {
      setSelectedMenu(menuItemKey as MenuItems);
      const path = menuItemKey === MenuItems.Home ? '/' : `/${menuItemKey}`;
      navigate(path);
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <Navbar>
      <NavLeft>
        <Link to="/" onClick={() => setSelectedMenu(MenuItems.Home)}>
          <LogoImage src={Logo} alt="Logo" />
        </Link>

        <DesktopNav>
          <NavList>
            {menuItems.map((menuItem) => (
              <NavItem
                key={menuItem.key}
                $isActive={activeMenuKey === menuItem.key}
                onClick={() => handleMenuClick(menuItem.key)}
              >
                {menuItem.label}
              </NavItem>
            ))}
          </NavList>
        </DesktopNav>
      </NavLeft>

      <ActionButtons>
        {isLoggedIn ? (
          <>
            <UserName>{userLabel}</UserName>
            <OutlineButton onClick={handleLogout}>
              <ArrowRightStartOnRectangleIcon style={{ width: 16, height: 16 }} />
              Odhlásit se
            </OutlineButton>
          </>
        ) : (
          <>
            <OutlineButton onClick={() => navigate("/register")}>
              <UserIcon style={{ width: 16, height: 16 }} />
              Vytvořit účet
            </OutlineButton>
            <FilledButton onClick={() => navigate("/login")}>
              <UserIcon style={{ width: 16, height: 16 }} />
              Přihlásit se
            </FilledButton>
          </>
        )}
      </ActionButtons>

      <MobileActions>
        {isLoggedIn ? (
          <IconButton
            $variant="primary"
            onClick={handleLogout}
            aria-label="Odhlásit se"
          >
            <ArrowRightStartOnRectangleIcon style={{ width: 18, height: 18 }} />
          </IconButton>
        ) : (
          <IconButton
            $variant="primary"
            onClick={() => navigate("/login")}
            aria-label="Přihlásit se"
          >
            <UserIcon style={{ width: 18, height: 18 }} />
          </IconButton>
        )}
        <IconButton
          $variant="neutral"
          onClick={showDrawer}
          aria-label="Open menu"
        >
          <Bars3Icon style={{ width: 20, height: 20 }} />
        </IconButton>
      </MobileActions>

      {/* Mobile Menu Dialog (Slide-over from right) */}
      <Transition.Root show={isMobileMenuOpen} as={Fragment}>
        <Dialog
          as="div"
          onClose={closeDrawer}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0, 0, 0, 0.6)",
              }}
            />
          </Transition.Child>

          <div
            style={{
              position: "fixed",
              inset: 0,
              overflowY: "auto",
            }}
          >
            <div
              style={{
                minHeight: "100%",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                padding: 16,
                paddingTop: 96,
              }}
            >
              <Transition.Child
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel style={{ width: "100%" }}>
                  <MobileMenuPanel>
                    <MobileMenuContainer>
                      {menuItems.map((menuItem) => (
                        <MobileMenuItem
                          key={menuItem.key}
                          $isActive={activeMenuKey === menuItem.key}
                          onClick={() => handleMenuClick(menuItem.key)}
                        >
                          {menuItem.label}
                        </MobileMenuItem>
                      ))}
                      {!isLoggedIn && (
                        <>
                          <MobileMenuItem
                            onClick={() => {
                              navigate("/register");
                              setIsMobileMenuOpen(false);
                            }}
                          >
                            Vytvořit účet
                          </MobileMenuItem>
                          <MobileMenuItem
                            onClick={() => {
                              navigate("/login");
                              setIsMobileMenuOpen(false);
                            }}
                          >
                            Přihlásit se
                          </MobileMenuItem>
                        </>
                      )}
                      {isLoggedIn && (
                        <MobileMenuItem
                          onClick={() => {
                            handleLogout();
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          Odhlásit se
                        </MobileMenuItem>
                      )}
                    </MobileMenuContainer>
                  </MobileMenuPanel>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </Navbar>
  );
};

export default MenuBar;
